import { ColDef, IsFullWidthRowParams, SelectionChangedEvent } from 'ag-grid-community';
import { AgGridReact, AgGridReactProps, CustomCellRendererProps } from 'ag-grid-react';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Remove as RemoveIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { ArrayFilter } from '../AgGrid/ArrayFilter';
import { SyncPlanStatus } from '../../../../types/constants/sync';

type Plan = { invalid?: Record<string, string[]>; create: object[]; update: object[]; delete: object[] };
type InvalidRow = { name: string; errors: string[]; status: SyncPlanStatus.INVALID };
type CreateRow<T extends Plan> = T['create'][number] & { status: SyncPlanStatus.CREATE; selected: boolean };
type UpdateRow<T extends Plan> = T['update'][number] & { status: SyncPlanStatus.UPDATE; selected: boolean };
type DeleteRow<T extends Plan> = T['delete'][number] & { status: SyncPlanStatus.DELETE; selected: boolean };
type SyncRow<T extends Plan> = InvalidRow | CreateRow<T> | UpdateRow<T> | DeleteRow<T>;

interface Props<T extends Plan> {
  plan: T;
  columns: ColDef<Omit<SyncRow<T>, 'status' | 'selected'>>[];
  onPlanChanged?: (newPlan: T) => void;
  gridProps?: AgGridReactProps<SyncRow<T>>;
}

export const SyncPlan = <T extends Plan>({ plan, columns: initialColumns, onPlanChanged, gridProps }: Props<T>) => {
  const columns = useMemo<ColDef<SyncRow<T>>[]>(
    () => [
      {
        field: 'status',
        headerName: '',
        pinned: 'left',
        filter: ArrayFilter,
        filterParams: { values: Object.values(SyncPlanStatus).filter((status) => status !== SyncPlanStatus.INVALID) },
        maxWidth: 60,
        cellRenderer: ({ data }: CustomCellRendererProps<SyncRow<T>>) => {
          switch (data?.status) {
            case 'create':
              return <AddIcon sx={{ color: 'green', verticalAlign: 'middle' }} />;
            case 'update':
              return <EditIcon sx={{ color: 'blue', verticalAlign: 'middle' }} />;
            case 'delete':
              return <RemoveIcon sx={{ color: 'red', verticalAlign: 'middle' }} />;
            default:
              return null;
          }
        },
      },
      ...(initialColumns as ColDef<SyncRow<T>>[]),
    ],
    [initialColumns],
  );

  const [modifiedPlan] = useState<SyncRow<T>[]>([
    ...Object.entries(plan.invalid ?? {}).map<InvalidRow>(([name, errors]) => ({
      name,
      errors,
      status: SyncPlanStatus.INVALID,
    })),
    ...plan.create.map<CreateRow<T>>((sync) => ({ ...sync, status: SyncPlanStatus.CREATE, selected: true })),
    ...plan.update.map<UpdateRow<T>>((sync) => ({ ...sync, status: SyncPlanStatus.UPDATE, selected: true })),
    ...plan.delete.map<DeleteRow<T>>((sync) => ({ ...sync, status: SyncPlanStatus.DELETE, selected: true })),
  ]);

  const handleSelectionChange = ({ api }: SelectionChangedEvent<SyncRow<T>>) => {
    const selectedRows = api.getSelectedRows();
    onPlanChanged?.({
      create: selectedRows
        .filter((row): row is CreateRow<T> => row.status === SyncPlanStatus.CREATE && row.selected)
        .map(({ status, selected, ...create }) => create),
      update: selectedRows
        .filter((row): row is UpdateRow<T> => row.status === SyncPlanStatus.UPDATE && row.selected)
        .map(({ status, selected, ...update }) => update),
      delete: selectedRows
        .filter((row): row is DeleteRow<T> => row.status === SyncPlanStatus.DELETE && row.selected)
        .map(({ status, selected, ...del }) => del),
    } as T);
  };

  return (
    <>
      <Box flexGrow={1} sx={{ height: '300px', alignSelf: 'stretch', pb: 2, pt: 2 }}>
        <AgGridReact
          {...gridProps}
          columnDefs={columns}
          defaultColDef={{ flex: 1, minWidth: 100, sortable: false }}
          rowData={modifiedPlan}
          rowSelection={{
            mode: 'multiRow',
            selectAll: 'filtered',
            isRowSelectable: (rowNode) => rowNode.data?.status !== SyncPlanStatus.INVALID,
          }}
          selectionColumnDef={{ pinned: 'left' }}
          onSelectionChanged={handleSelectionChange}
          onFirstDataRendered={({ api }) => api.selectAll()}
          embedFullWidthRows
          isFullWidthRow={({ rowNode }: IsFullWidthRowParams<InvalidRow>) =>
            rowNode.data?.status === SyncPlanStatus.INVALID
          }
          fullWidthCellRenderer={({ data, pinned }: CustomCellRendererProps<InvalidRow>) => (
            <Box display="flex" sx={{ height: '100%', alignItems: 'center' }}>
              {pinned === 'left' ? (
                <WarningAmberIcon sx={{ color: 'orange', width: '100%' }} />
              ) : (
                <Typography>{`${data?.name}: ${data?.errors.join(', ')}`}</Typography>
              )}
            </Box>
          )}
        />
      </Box>
    </>
  );
};
